import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';

import { skip, Subscription } from 'rxjs';

import { PhonepeFastagSearchService } from '../../../services/phonepe-fastag/phonepe-fastag.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';

@Component({
  selector: 'app-osint-phonepe-fastag-search-result',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    HeaderComponent,
    SidebarComponent,
  ],
  templateUrl: './osint-phonepe-fastag-search-result.component.html',
  styleUrl: './osint-phonepe-fastag-search-result.component.css',
})
export class OsintPhonepeFastagSearchResultComponent
  implements OnInit, OnDestroy
{
  jobId: string = '';
  searchId: string = '';
  selectedSearchResult: any;
  phonepeFastagData: any = null;
  noRecords = false;

  loadingCompletedSearchResult: boolean = false;
  loadingPolledSearchResult: boolean = false;

  jobResultSubscription?: Subscription;

  get isLoadingResults(): boolean {
    const hasResults = !!this.phonepeFastagData;
    return (
      (this.loadingCompletedSearchResult || this.loadingPolledSearchResult) &&
      !hasResults &&
      !this.noRecords
    );
  }

  notes: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private searchPollingService: SearchPollingService,
    private phonepeFastagSearchService: PhonepeFastagSearchService,
    public utilsService: UtilsService,
  ) {}

  ngOnInit() {
    this.selectedSearchResult = history.state?.searchResult;
    this.jobId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.selectedSearchResult && !this.jobId) {
      this.router.navigate(['/phonepe-fastag-search']);
      return;
    }

    if (this.selectedSearchResult) {
      this.searchId = this.selectedSearchResult.phonepe_fastag_search_id;

      if (this.selectedSearchResult.done) {
        this.getCompletedSearchResult();
      } else {
        this.pollForSearchResult();
      }
    } else if (this.jobId) {
      this.pollForSearchResult();
    }
  }

  ngOnDestroy() {
    this.jobResultSubscription?.unsubscribe();
  }

  onBackButtonClick() {
    this.router.navigate(['/phonepe-fastag-search']);
  }

  getCompletedSearchResult() {
    if (!this.searchId && !this.jobId) {
      return;
    }

    this.loadingCompletedSearchResult = true;

    this.phonepeFastagSearchService
      .getCompletedSearchResult(this.searchId, this.jobId)
      .subscribe({
        next: (response) => {
          const rawSearchResultData = response?.data;
          const completedSearchResult = rawSearchResultData?.data_list?.[0];

          if (!completedSearchResult) {
            this.noRecords = true;
            this.loadingCompletedSearchResult = false;
            this.loadingPolledSearchResult = false;
            return;
          }

          if (
            completedSearchResult?.phonepe_fastag_search_id &&
            !this.searchId
          ) {
            this.searchId = completedSearchResult.phonepe_fastag_search_id;
          }

          this.selectedSearchResult = {
            ...this.selectedSearchResult,
            ...completedSearchResult,
            searched_vehicle:
              completedSearchResult?.phonepe_fastag_data?.vehicle_number ||
              this.selectedSearchResult?.searched_vehicle,
          };

          this.notes =
            rawSearchResultData?.notes || completedSearchResult?.notes || '';
          this.phonepeFastagData =
            completedSearchResult?.phonepe_fastag_data || null;
          this.noRecords =
            !this.phonepeFastagData?.found &&
            !this.hasAnyField(this.phonepeFastagData);

          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;
        },
        error: (error) => {
          console.error(error);
          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;
        },
      });
  }

  private hasAnyField(data: any): boolean {
    if (!data) return false;
    return Boolean(
      data.vehicle_number ||
        data.owner_name ||
        data.tag_id ||
        data.tag_status ||
        data.issue_date ||
        data.vehicle_class ||
        data.vehicle_class_code ||
        data.bank ||
        data.bank_id ||
        data.biller_id ||
        data.vpa,
    );
  }

  pollForSearchResult() {
    if (!this.jobId) {
      this.jobId = this.selectedSearchResult?.job_id;
    }

    if (!this.jobId) {
      console.error('No job ID available for polling');
      return;
    }

    this.searchPollingService.startPolling([this.jobId], (ids) =>
      this.phonepeFastagSearchService.getSearchResults(ids),
    );

    this.jobResultSubscription?.unsubscribe();
    this.loadingPolledSearchResult = true;

    this.jobResultSubscription = this.searchPollingService
      .getJobResult(this.jobId)
      .pipe(skip(1))
      .subscribe({
        next: (response) => {
          if (!response) {
            return;
          }

          if (response.phonepe_fastag_search_id && !this.searchId) {
            this.searchId = response.phonepe_fastag_search_id;
          }

          this.selectedSearchResult = {
            ...this.selectedSearchResult,
            done: response.done || false,
            status: response.status || 'pending',
            searched_vehicle:
              response?.searched_vehicle ||
              this.selectedSearchResult?.searched_vehicle,
            phonepe_fastag_search_id:
              response?.phonepe_fastag_search_id ||
              this.selectedSearchResult?.phonepe_fastag_search_id,
          };

          if (response.done === true || response.status === 'completed') {
            this.getCompletedSearchResult();
          }
        },
        error: (error) => {
          console.error(error);
          this.loadingPolledSearchResult = false;
        },
      });
  }

  onUpdateNotesButtonClick(): void {
    const resultId = this.selectedSearchResult?.result_id;

    if (!this.notes.trim() || !resultId) {
      this.snackBar.open(
        'Please enter notes and ensure result ID is available.',
        'Close',
        {
          duration: 3000,
          panelClass: ['snackbar-error'],
        },
      );
      return;
    }

    const payload = {
      result_id: resultId,
      notes: this.notes.trim(),
    };

    this.phonepeFastagSearchService.updatePhonepeFastagNotes(payload).subscribe({
      next: () => {
        this.snackBar.open('Notes updated successfully!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
        });
      },
      error: () => {
        this.snackBar.open('Failed to update notes.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      },
    });
  }

  formatDisplay(value: any): string {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      if (this.utilsService?.formatDateTime) {
        return this.utilsService.formatDateTime(dateString, 'MMM d, y');
      }
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }
}
